/**
 * Unified App Storage
 * 
 * Provides async key-value storage that:
 * - In Electron: Uses file system via IPC (userData/app-store.json)
 * - In Browser: Uses IndexedDB (async, ~50MB+, survives cache clears better than localStorage)
 * 
 * All methods are async to support both backends uniformly.
 */

const DB_NAME = 'consl-app-storage';
const DB_VERSION = 1;
const STORE_NAME = 'keyvalue';

// IndexedDB wrapper for browser fallback
class IndexedDBStorage {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private openDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
    });

    return this.dbPromise;
  }

  async getItem<T>(key: string): Promise<T | null> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result ?? null);
      });
    } catch (error) {
      console.warn('IndexedDB getItem error:', error);
      return null;
    }
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(value, key);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.warn('IndexedDB setItem error:', error);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.warn('IndexedDB removeItem error:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.warn('IndexedDB clear error:', error);
    }
  }
}

// Electron IPC wrapper
class ElectronStorage {
  private api: {
    getItem: (key: string) => Promise<unknown>;
    setItem: (key: string, value: unknown) => Promise<void>;
    removeItem: (key: string) => Promise<void>;
    clear: () => Promise<void>;
  };

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.api = (window as any).electronAPI.appStorage;
  }

  async getItem<T>(key: string): Promise<T | null> {
    try {
      const value = await this.api.getItem(key);
      return (value as T) ?? null;
    } catch (error) {
      console.warn('Electron storage getItem error:', error);
      return null;
    }
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    try {
      await this.api.setItem(key, value);
    } catch (error) {
      console.warn('Electron storage setItem error:', error);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      await this.api.removeItem(key);
    } catch (error) {
      console.warn('Electron storage removeItem error:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      await this.api.clear();
    } catch (error) {
      console.warn('Electron storage clear error:', error);
    }
  }
}

// Storage interface
export interface AppStorageInterface {
  getItem<T>(key: string): Promise<T | null>;
  setItem<T>(key: string, value: T): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

// Storage that always prefers Electron when available (re-checks on each call)
// This handles the case where storage is accessed before electronAPI is ready
class LazyAppStorage implements AppStorageInterface {
  private electronStorage: ElectronStorage | null = null;
  private browserStorage: IndexedDBStorage | null = null;
  private memoryStorage: AppStorageInterface | null = null;

  private isElectronAvailable(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return typeof window !== 'undefined' && !!(window as any).electronAPI?.appStorage;
  }

  private getStorage(): AppStorageInterface {
    // Always prefer Electron if available (even if we previously used IndexedDB)
    if (this.isElectronAvailable()) {
      if (!this.electronStorage) {
        this.electronStorage = new ElectronStorage();
      }
      return this.electronStorage;
    }

    // Browser fallback - IndexedDB
    if (typeof indexedDB !== 'undefined') {
      if (!this.browserStorage) {
        this.browserStorage = new IndexedDBStorage();
      }
      return this.browserStorage;
    }

    // Ultimate fallback - in-memory
    if (!this.memoryStorage) {
      console.warn('No persistent storage available, using in-memory storage');
      const memoryStore = new Map<string, unknown>();
      this.memoryStorage = {
        getItem: async <T>(key: string) => (memoryStore.get(key) as T) ?? null,
        setItem: async <T>(key: string, value: T) => { memoryStore.set(key, value); },
        removeItem: async (key: string) => { memoryStore.delete(key); },
        clear: async () => { memoryStore.clear(); },
      };
    }
    return this.memoryStorage;
  }

  async getItem<T>(key: string): Promise<T | null> {
    return this.getStorage().getItem<T>(key);
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    return this.getStorage().setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    return this.getStorage().removeItem(key);
  }

  async clear(): Promise<void> {
    return this.getStorage().clear();
  }
}

// Export singleton instance (lazy-initialized)
export const appStorage = new LazyAppStorage();

// Storage keys enum for type safety
export const StorageKeys = {
  TABS: 'tabs',
  ACTIVE_TAB_ID: 'activeTabId',
} as const;

export type StorageKey = typeof StorageKeys[keyof typeof StorageKeys];
